import React, { useEffect, useState, type ReactNode } from 'react';
import { toProjectType } from '@atlassian/jira-common-constants/src/project-types';
import { COHORT, NOT_ENROLLED, VARIATION } from '@atlassian/jira-common-util-get-experiment-group';
import { UNSAFE_noExposureExp } from '@atlassian/jira-feature-experiments';
import { useProject } from '@atlassian/jira-navigation-apps-sidebar-common/src/controllers/project/index.tsx';
import { useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { fireTrackAnalyticsEvent } from './common/analytics/utils';
import { useCoordinationClient } from './services/coordination-client';

type EPCheckProps = {
	onComplete: () => void;
	children: ReactNode;
};
export const EPCheck = ({ onComplete, children }: EPCheckProps) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const [expConfig] = UNSAFE_noExposureExp('cross-flow-defer-edition-communication');
	const cohort = expConfig.get(COHORT, NOT_ENROLLED);

	const { data: project } = useProject();
	const projectType = toProjectType(project?.type);

	const [hasMessageStarted, setHasMessageStarted] = useState(false);
	const [hasCheckedEpMessage, setHasCheckedEpMessage] = useState(false);
	const { start: checkEPMessage } = useCoordinationClient(projectType);

	const isEnrolled = cohort === VARIATION;

	useEffect(() => {
		// reset checks when we switch projects
		setHasCheckedEpMessage(false);
		setHasMessageStarted(false);
	}, [projectType]);

	useEffect(() => {
		if (!isEnrolled || hasCheckedEpMessage || projectType === undefined) return;

		(async () => {
			setHasCheckedEpMessage(true);
			const hasStarted = await checkEPMessage();

			fireTrackAnalyticsEvent(createAnalyticsEvent, 'engagementPlatformMessage checked', {
				projectType,
				cohort,
				hasCheckedEpMessage,
				hasEPStarted: hasStarted,
			});

			setHasMessageStarted(hasStarted);
			hasStarted && onComplete();
		})();
	}, [
		createAnalyticsEvent,
		checkEPMessage,
		hasCheckedEpMessage,
		isEnrolled,
		onComplete,
		projectType,
		cohort,
	]);

	return <>{hasMessageStarted && children}</>;
};
